import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ArrowRightLeft, Plus, CheckCircle, AlertTriangle, Clock, X,
  Loader2, MessageSquare, Wrench, User, Shield, RotateCcw,
  Sun, Sunset, Moon, Trash2,
} from 'lucide-react'
import { useAuthStore } from '@/store/auth'
import { apiGet, apiPost, apiPatch } from '@/lib/api'
import { cn, formatDate, formatDateTime } from '@/lib/utils'

interface HandoverItem {
  id: string
  sequence: number
  category: string
  title: string
  description: string | null
  priority: string
  workOrderId: string | null
  isResolved: boolean
  handover?: { shiftDate: string; shiftType: string; createdBy: { firstName: string; lastName: string } }
}

interface Handover {
  id: string
  shiftType: string
  shiftDate: string
  status: string
  summary: string | null
  createdAt: string
  acknowledgedAt: string | null
  createdBy: { id: string; firstName: string; lastName: string; role: string }
  acknowledgedBy: { id: string; firstName: string; lastName: string } | null
  items: HandoverItem[]
}

const SHIFT_ICONS: Record<string, typeof Sun> = { DAY: Sun, EVENING: Sunset, NIGHT: Moon }
const SHIFT_COLORS: Record<string, string> = {
  DAY: 'bg-amber-100 text-amber-800',
  EVENING: 'bg-orange-100 text-orange-800',
  NIGHT: 'bg-indigo-100 text-indigo-800',
}

const ITEM_CATEGORIES = [
  { value: 'ISSUE', label: 'Issue', icon: AlertTriangle, color: 'text-red-500' },
  { value: 'INCOMPLETE_WO', label: 'Incomplete WO', icon: Wrench, color: 'text-amber-500' },
  { value: 'GUEST_REQUEST', label: 'Guest Request', icon: User, color: 'text-blue-500' },
  { value: 'SAFETY', label: 'Safety', icon: Shield, color: 'text-red-600' },
  { value: 'FOLLOW_UP', label: 'Follow Up', icon: RotateCcw, color: 'text-purple-500' },
  { value: 'NOTE', label: 'Note', icon: MessageSquare, color: 'text-gray-500' },
]

const PRIORITY_BADGE: Record<string, string> = {
  URGENT: 'bg-red-100 text-red-800',
  HIGH: 'bg-amber-100 text-amber-800',
  STANDARD: 'bg-blue-100 text-blue-800',
  LOW: 'bg-gray-100 text-gray-600',
}

export function HandoverPage() {
  const { currentPropertyId, user } = useAuthStore()
  const pid = currentPropertyId
  const queryClient = useQueryClient()

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [shiftType, setShiftType] = useState('DAY')
  const [shiftDate, setShiftDate] = useState(new Date().toISOString().slice(0, 10))
  const [summary, setSummary] = useState('')
  const [items, setItems] = useState<{ category: string; title: string; description: string; priority: string }[]>([])

  // ── Queries ─────────────────────────────────────────────────────────────
  const { data: currentData, isLoading: currentLoading } = useQuery({
    queryKey: ['handover-current', pid],
    queryFn: () => apiGet<{ handover: Handover | null; unresolvedItems: HandoverItem[] }>(`/properties/${pid}/handover/current`),
    enabled: !!pid,
  })

  const { data: history, isLoading: historyLoading } = useQuery({
    queryKey: ['handover-history', pid],
    queryFn: () => apiGet<Handover[]>(`/properties/${pid}/handover?days=14`),
    enabled: !!pid,
  })

  // ── Mutations ───────────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (body: Record<string, unknown>) => apiPost(`/properties/${pid}/handover`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['handover-current'] })
      queryClient.invalidateQueries({ queryKey: ['handover-history'] })
      setShowCreateModal(false)
      resetForm()
    },
  })

  const acknowledgeMutation = useMutation({
    mutationFn: (handoverId: string) => apiPatch(`/properties/${pid}/handover/${handoverId}/acknowledge`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['handover-current'] })
      queryClient.invalidateQueries({ queryKey: ['handover-history'] })
    },
  })

  const resolveMutation = useMutation({
    mutationFn: (itemId: string) => apiPatch(`/properties/${pid}/handover/items/${itemId}/resolve`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['handover-current'] })
      queryClient.invalidateQueries({ queryKey: ['handover-history'] })
    },
  })

  function resetForm() {
    setSummary('')
    setItems([])
    setShiftType('DAY')
    setShiftDate(new Date().toISOString().slice(0, 10))
  }

  function addItem() {
    setItems([...items, { category: 'NOTE', title: '', description: '', priority: 'STANDARD' }])
  }

  function updateItem(idx: number, field: string, value: string) {
    setItems(items.map((item, i) => i === idx ? { ...item, [field]: value } : item))
  }

  function removeItem(idx: number) {
    setItems(items.filter((_, i) => i !== idx))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const validItems = items.filter(i => i.title.trim())
    createMutation.mutate({
      shiftType,
      shiftDate,
      summary: summary || undefined,
      items: validItems,
    })
  }

  const current = currentData?.handover
  const unresolved = currentData?.unresolvedItems ?? []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-navy-800">Shift Handover</h1>
          <p className="text-gray-500 text-sm mt-0.5">Log issues and notes for the incoming shift</p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary">
          <Plus className="w-4 h-4" />
          Create Handover
        </button>
      </div>

      {/* Current handover card */}
      <div className="card border-navy-200">
        <h2 className="text-sm font-semibold text-navy-800 mb-4 flex items-center gap-2">
          <ArrowRightLeft className="w-4 h-4 text-navy-400" />
          Incoming Handover
        </h2>

        {currentLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : current ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={cn('badge', SHIFT_COLORS[current.shiftType])}>
                  {current.shiftType} Shift
                </span>
                <span className="text-sm text-gray-500">{formatDate(current.shiftDate)}</span>
                <span className="text-sm text-gray-400">
                  by {current.createdBy.firstName} {current.createdBy.lastName}
                </span>
              </div>
              <div className="flex items-center gap-2">
                {current.status === 'ACKNOWLEDGED' ? (
                  <span className="badge bg-green-100 text-green-800 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Acknowledged
                  </span>
                ) : (
                  <button
                    onClick={() => acknowledgeMutation.mutate(current.id)}
                    disabled={acknowledgeMutation.isPending}
                    className="btn-primary py-1 px-3 text-xs"
                  >
                    <CheckCircle className="w-3.5 h-3.5" />
                    Acknowledge
                  </button>
                )}
              </div>
            </div>

            {current.summary && (
              <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-3">{current.summary}</p>
            )}

            {current.items.length > 0 && (
              <div className="space-y-2">
                {current.items.map((item) => {
                  const catConfig = ITEM_CATEGORIES.find(c => c.value === item.category)
                  const Icon = catConfig?.icon ?? MessageSquare
                  return (
                    <div key={item.id} className={cn(
                      'flex items-start gap-3 p-3 rounded-lg border',
                      item.isResolved ? 'bg-gray-50 border-gray-100 opacity-60' : 'border-gray-200'
                    )}>
                      <Icon className={cn('w-4 h-4 mt-0.5 flex-shrink-0', catConfig?.color)} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={cn('badge', PRIORITY_BADGE[item.priority])}>{item.priority}</span>
                          <span className="badge bg-gray-100 text-gray-600">{catConfig?.label}</span>
                          {item.isResolved && <span className="badge bg-green-100 text-green-700">Resolved</span>}
                        </div>
                        <p className={cn('text-sm font-medium', item.isResolved ? 'text-gray-400 line-through' : 'text-navy-800')}>
                          {item.title}
                        </p>
                        {item.description && <p className="text-xs text-gray-500 mt-0.5">{item.description}</p>}
                      </div>
                      {!item.isResolved && (
                        <button
                          onClick={() => resolveMutation.mutate(item.id)}
                          className="text-gray-400 hover:text-green-600 p-1"
                          title="Mark resolved"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        ) : (
          <p className="text-sm text-gray-400 text-center py-4">No handover from the previous shift</p>
        )}
      </div>

      {/* Unresolved items from previous handovers */}
      {unresolved.length > 0 && (
        <div className="card border-amber-200">
          <h2 className="text-sm font-semibold text-navy-800 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Unresolved Items ({unresolved.length})
          </h2>
          <div className="space-y-2">
            {unresolved.map((item) => {
              const catConfig = ITEM_CATEGORIES.find(c => c.value === item.category)
              const Icon = catConfig?.icon ?? MessageSquare
              return (
                <div key={item.id} className="flex items-start gap-3 p-3 rounded-lg border border-amber-100 bg-amber-50/30">
                  <Icon className={cn('w-4 h-4 mt-0.5 flex-shrink-0', catConfig?.color)} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-navy-800">{item.title}</p>
                    {item.description && <p className="text-xs text-gray-500">{item.description}</p>}
                    {item.handover && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        From {item.handover.shiftType} shift {formatDate(item.handover.shiftDate)} — {item.handover.createdBy.firstName} {item.handover.createdBy.lastName}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => resolveMutation.mutate(item.id)}
                    className="text-gray-400 hover:text-green-600 p-1"
                    title="Mark resolved"
                  >
                    <CheckCircle className="w-4 h-4" />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* History */}
      <div className="card">
        <h2 className="text-sm font-semibold text-navy-800 mb-4">Recent Handovers (14 days)</h2>
        {historyLoading ? (
          <Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" />
        ) : !history?.length ? (
          <p className="text-sm text-gray-400 text-center py-4">No handovers yet</p>
        ) : (
          <div className="space-y-3">
            {history.map((h) => {
              const ShiftIcon = SHIFT_ICONS[h.shiftType] ?? Sun
              return (
                <div key={h.id} className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center', SHIFT_COLORS[h.shiftType])}>
                    <ShiftIcon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-navy-800">
                        {h.shiftType} Shift — {formatDate(h.shiftDate)}
                      </p>
                      {h.status === 'ACKNOWLEDGED' ? (
                        <span className="badge bg-green-100 text-green-800">Acknowledged</span>
                      ) : (
                        <span className="badge bg-amber-100 text-amber-800">Pending</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400">
                      {h.createdBy.firstName} {h.createdBy.lastName} — {h.items.length} items
                      {h.items.filter(i => !i.isResolved).length > 0 && (
                        <span className="text-amber-600 ml-2">
                          ({h.items.filter(i => !i.isResolved).length} unresolved)
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Create Handover Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-navy-800">Create Shift Handover</h2>
              <button onClick={() => { setShowCreateModal(false); resetForm() }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Shift</label>
                  <div className="flex gap-2">
                    {(['DAY', 'EVENING', 'NIGHT'] as const).map(s => {
                      const Icon = SHIFT_ICONS[s]
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setShiftType(s)}
                          className={cn(
                            'flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-medium transition-colors',
                            shiftType === s ? SHIFT_COLORS[s] : 'bg-gray-50 text-gray-400 hover:bg-gray-100'
                          )}
                        >
                          <Icon className="w-3.5 h-3.5" />
                          {s}
                        </button>
                      )
                    })}
                  </div>
                </div>
                <div>
                  <label className="label">Date</label>
                  <input type="date" value={shiftDate} onChange={e => setShiftDate(e.target.value)} className="input" />
                </div>
              </div>

              <div>
                <label className="label">Shift Summary</label>
                <textarea
                  value={summary}
                  onChange={e => setSummary(e.target.value)}
                  rows={2}
                  className="input resize-none"
                  placeholder="Brief summary of the shift — key events, overall status..."
                />
              </div>

              {/* Items */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="label mb-0">Handover Items</label>
                  <button type="button" onClick={addItem} className="btn-secondary py-1 px-2.5 text-xs">
                    <Plus className="w-3.5 h-3.5" /> Add Item
                  </button>
                </div>

                {items.length === 0 && (
                  <p className="text-sm text-gray-400 text-center py-4 border border-dashed border-gray-200 rounded-lg">
                    No items yet — click "Add Item" to log issues, notes, or incomplete work
                  </p>
                )}

                <div className="space-y-3">
                  {items.map((item, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-lg p-3 space-y-2">
                      <div className="flex gap-2">
                        <select
                          value={item.category}
                          onChange={e => updateItem(idx, 'category', e.target.value)}
                          className="input w-auto text-xs"
                        >
                          {ITEM_CATEGORIES.map(c => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </select>
                        <select
                          value={item.priority}
                          onChange={e => updateItem(idx, 'priority', e.target.value)}
                          className="input w-auto text-xs"
                        >
                          <option value="URGENT">Urgent</option>
                          <option value="HIGH">High</option>
                          <option value="STANDARD">Standard</option>
                          <option value="LOW">Low</option>
                        </select>
                        <input
                          value={item.title}
                          onChange={e => updateItem(idx, 'title', e.target.value)}
                          className="input flex-1 text-sm"
                          placeholder="What needs to be handed over?"
                        />
                        <button type="button" onClick={() => removeItem(idx)} className="text-gray-400 hover:text-red-500 p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      <input
                        value={item.description}
                        onChange={e => updateItem(idx, 'description', e.target.value)}
                        className="input text-xs"
                        placeholder="Additional details (optional)"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => { setShowCreateModal(false); resetForm() }} className="btn-secondary">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending}
                  className="btn-primary"
                >
                  {createMutation.isPending ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
                  ) : (
                    <><ArrowRightLeft className="w-4 h-4" /> Submit Handover</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
